export const CAJA_SESION_STORAGE = "CajaSesionActivaId";
export const ZONA_HORARIA_CAJA = "America/Argentina/Buenos_Aires";

const fechaConHoraSinZona = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

export const fechaCajaParaInput = (valor = new Date()) => {
  const fecha = valor instanceof Date ? valor : new Date(valor);
  if (Number.isNaN(fecha.getTime())) return "";
  const partes = new Intl.DateTimeFormat("en", {
    timeZone: ZONA_HORARIA_CAJA,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(fecha);
  const tomar = (tipo) => partes.find((parte) => parte.type === tipo)?.value || "";
  return `${tomar("year")}-${tomar("month")}-${tomar("day")}`;
};

export const formatearFechaCaja = (valor) => {
  if (!valor) return "—";
  const esHoraLocalSinZona = typeof valor === "string" && fechaConHoraSinZona.test(valor);
  const fecha = new Date(esHoraLocalSinZona ? `${valor}Z` : valor);
  if (Number.isNaN(fecha.getTime())) return "—";
  const partes = new Intl.DateTimeFormat("en", {
    timeZone: ZONA_HORARIA_CAJA,
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(fecha);
  const tomar = (tipo) => partes.find((parte) => parte.type === tipo)?.value || "";
  return `${tomar("day")}/${tomar("month")}/${tomar("year")} ${tomar("hour")}:${tomar("minute")}`;
};

export const idsOperadoresCaja = ({ sesion = {}, resumen = {} } = {}) => {
  const ids = [
    ...(resumen.operadores || []).map((operador) => operador.operadorId),
    ...(sesion.operadoresIds || []),
    sesion.operadorAperturaId,
    sesion.operadorCierreId,
  ];
  return [...new Set(ids.map(Number).filter((id) => id > 0))];
};

export const respuestaCaja = (respuesta) => {
  const datos = respuesta?.data;
  const error = respuesta?.error || datos?.error || datos?.Error || "";
  return { error, datos };
};

const numeroOpcional = (valor) => {
  if (valor === null || valor === undefined || valor === "") return null;
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : null;
};

export const normalizarCaja = (caja = {}) => ({
  id: Number(caja.id ?? caja.Id ?? caja.cajaId ?? caja.CajaId) || 0,
  empresaId: Number(caja.empresaId ?? caja.EmpresaId) || 0,
  codigo: String(caja.codigo ?? caja.Codigo ?? caja.cajaCodigo ?? caja.CajaCodigo ?? ""),
  descripcion: String(caja.descripcion ?? caja.Descripcion ?? caja.cajaDescripcion ?? caja.CajaDescripcion ?? ""),
  modo: String(caja.modo ?? caja.Modo ?? "Compartida"),
  pcId: numeroOpcional(caja.pcId ?? caja.PcId ?? caja.PCId),
  puntoVentaId: numeroOpcional(caja.puntoVentaId ?? caja.PuntoVentaId),
  puntoVenta: String(caja.puntoVenta ?? caja.PuntoVenta ?? ""),
  gestionAutomatica: Boolean(caja.gestionAutomatica ?? caja.GestionAutomatica),
  esPredeterminada: Boolean(caja.esPredeterminada ?? caja.EsPredeterminada),
  activo: Boolean(caja.activo ?? caja.Activo),
});

export const extraerCajas = (respuesta) => {
  const datos = respuesta?.data ?? respuesta;
  const lista = datos?.lista ?? datos?.Lista ?? [];
  return Array.isArray(lista) ? lista.map(normalizarCaja) : [];
};

export const normalizarFormaPagoCaja = (formaPago = {}) => ({
  formaPagoId: Number(formaPago.formaPagoId ?? formaPago.FormaPagoId) || 0,
  codigo: String(formaPago.codigo ?? formaPago.Codigo ?? ""),
  descripcion: String(formaPago.descripcion ?? formaPago.Descripcion ?? ""),
  impactaArqueo: Boolean(formaPago.impactaArqueo ?? formaPago.ImpactaArqueo),
  activo: Boolean(formaPago.activo ?? formaPago.Activo),
});

export const extraerFormasPagoCaja = (respuesta) => {
  const datos = respuesta?.data ?? respuesta;
  const lista = datos?.lista ?? datos?.Lista ?? [];
  return Array.isArray(lista) ? lista.map(normalizarFormaPagoCaja) : [];
};

export const DEBOUNCE_OPERADORES_CAJA_MS = 350;

export const normalizarOperadorCaja = (operador = {}) => {
  const nombre = String(operador.Nombre ?? operador.nombre ?? "").trim();
  const apellido = String(operador.Apellido ?? operador.apellido ?? "").trim();
  const nombreCompletoInformado = String(operador.NombreCompleto ?? operador.nombreCompleto ?? "").trim();
  const mail = String(operador.Mail ?? operador.mail ?? "").trim();
  const nombreCompleto = nombreCompletoInformado || [nombre, apellido].filter(Boolean).join(" ") || mail;
  return {
    id: Number(operador.Id ?? operador.id) || 0,
    nombre,
    apellido,
    nombreCompleto,
    mail,
    etiqueta: nombreCompleto || mail || "Operador sin nombre",
  };
};

export const extraerOperadoresDisponiblesCaja = (respuesta) => {
  const datos = respuesta?.data ?? respuesta;
  const lista = datos?.Lista ?? datos?.lista ?? [];
  return Array.isArray(lista)
    ? lista.map(normalizarOperadorCaja).filter((operador) => operador.id > 0)
    : [];
};

export const crearRequestOperadoresDisponiblesCaja = ({ EmpresaId, cajaSesionId, busqueda = "" }) => ({
  EmpresaId: Number(EmpresaId) || 0,
  CajaSesionId: Number(cajaSesionId) || 0,
  Busqueda: String(busqueda).trim(),
  Limite: 20,
});

export const crearRequestAgregarOperadorCaja = ({ EmpresaId, cajaSesionId, operadorId, operadorAccionId, pcId }) => ({
  EmpresaId: Number(EmpresaId) || 0,
  CajaSesionId: Number(cajaSesionId) || 0,
  OperadorId: Number(operadorId) || 0,
  OperadorAccionId: Number(operadorAccionId) || 0,
  PcId: Number(pcId) || 0,
});

export const crearRequestGuardarCaja = ({ EmpresaId, operadorId, pcId, item = {} }) => {
  const itemNormalizado = { ...item };
  delete itemNormalizado.empresaId;
  return {
    EmpresaId: Number(EmpresaId) || 0,
    operadorId: Number(operadorId) || 0,
    pcId: Number(pcId) || 0,
    item: {
      ...itemNormalizado,
      EmpresaId: Number(EmpresaId) || 0,
    },
  };
};

export const crearControladorBusquedaOperadoresCaja = ({
  consultar,
  alLimpiar = () => {},
  alBuscar = () => {},
  alResolver = () => {},
  alError = () => {},
  debounceMs = DEBOUNCE_OPERADORES_CAJA_MS,
}) => {
  let temporizador = null;
  let controlador = null;
  let secuencia = 0;
  let ultimaClave = null;

  const cancelar = () => {
    if (temporizador) clearTimeout(temporizador);
    temporizador = null;
    controlador?.abort();
    controlador = null;
  };

  const actualizar = (contexto = {}, { forzar = false } = {}) => {
    const request = crearRequestOperadoresDisponiblesCaja(contexto);
    const valido = Boolean(request.EmpresaId) && request.CajaSesionId > 0;
    const clave = `${request.EmpresaId}\u0000${request.CajaSesionId}\u0000${request.Busqueda}`;
    if (!forzar && valido && clave === ultimaClave) return;

    cancelar();
    secuencia += 1;
    const miSecuencia = secuencia;
    if (!valido) {
      ultimaClave = null;
      alLimpiar();
      return;
    }

    temporizador = setTimeout(async () => {
      temporizador = null;
      ultimaClave = clave;
      controlador = new AbortController();
      const controladorActual = controlador;
      alBuscar(request);
      try {
        const resultado = await consultar(request, controladorActual.signal);
        if (miSecuencia === secuencia && !controladorActual.signal.aborted) alResolver(resultado, request);
      } catch (error) {
        if (miSecuencia === secuencia && error?.name !== "AbortError") {
          ultimaClave = null;
          alError(error, request);
        }
      } finally {
        if (miSecuencia === secuencia) controlador = null;
      }
    }, debounceMs);
  };

  const destruir = () => {
    secuencia += 1;
    cancelar();
  };

  return { actualizar, destruir };
};

export const normalizarSesionCaja = (sesion = {}) => ({
  id: Number(sesion.id ?? sesion.Id ?? sesion.cajaSesionId ?? sesion.CajaSesionId) || 0,
  cajaId: Number(sesion.cajaId ?? sesion.CajaId) || 0,
  cajaCodigo: String(sesion.cajaCodigo ?? sesion.CajaCodigo ?? ""),
  cajaDescripcion: String(sesion.cajaDescripcion ?? sesion.CajaDescripcion ?? ""),
  modo: String(sesion.modo ?? sesion.Modo ?? ""),
  estado: String(sesion.estado ?? sesion.Estado ?? ""),
  fechaApertura: sesion.fechaApertura ?? sesion.FechaApertura ?? null,
  operadorAperturaId: Number(sesion.operadorAperturaId ?? sesion.OperadorAperturaId) || 0,
  saldoInicial: Number(sesion.saldoInicial ?? sesion.SaldoInicial) || 0,
  fechaCierre: sesion.fechaCierre ?? sesion.FechaCierre ?? null,
  operadorCierreId: numeroOpcional(sesion.operadorCierreId ?? sesion.OperadorCierreId),
  saldoSistema: Number(sesion.saldoSistema ?? sesion.SaldoSistema) || 0,
  saldoDeclarado: numeroOpcional(sesion.saldoDeclarado ?? sesion.SaldoDeclarado),
  diferencia: numeroOpcional(sesion.diferencia ?? sesion.Diferencia),
  ...(Array.isArray(sesion.operadoresIds ?? sesion.OperadoresIds)
    ? {
        operadoresIds: [
          ...new Set((sesion.operadoresIds ?? sesion.OperadoresIds).map(Number).filter((id) => id > 0)),
        ],
      }
    : {}),
});

export const enriquecerHistorialOperadores = async (
  sesiones = [],
  cargarResumen,
  { concurrencia = 4 } = {},
) => {
  const resultado = new Array(sesiones.length);
  let siguiente = 0;
  const cantidadWorkers = Math.min(Math.max(1, Number(concurrencia) || 1), sesiones.length);

  const worker = async () => {
    while (siguiente < sesiones.length) {
      const indice = siguiente;
      siguiente += 1;
      const sesion = sesiones[indice];
      const operadoresExistentes = idsOperadoresCaja({ sesion });
      if (Array.isArray(sesion.operadoresIds) && sesion.operadoresIds.length > 0) {
        resultado[indice] = { ...sesion, operadoresIds: operadoresExistentes };
        continue;
      }
      try {
        const resumen = await cargarResumen(sesion);
        resultado[indice] = {
          ...sesion,
          operadoresIds: idsOperadoresCaja({ sesion, resumen: resumen || {} }),
        };
      } catch {
        resultado[indice] = { ...sesion, operadoresIds: operadoresExistentes };
      }
    }
  };

  await Promise.all(Array.from({ length: cantidadWorkers }, worker));
  return resultado;
};

export const extraerHistorialCajas = (respuesta) => {
  const datos = respuesta?.data ?? respuesta;
  const lista = datos?.lista ?? datos?.Lista ?? [];
  return Array.isArray(lista) ? lista.map(normalizarSesionCaja) : [];
};

export const sesionCajaDeRespuesta = (datos) => {
  const sesion = datos?.sesion || datos?.Sesion || datos?.item || datos?.Item ||
    (Number(datos?.id ?? datos?.Id ?? datos?.cajaSesionId ?? datos?.CajaSesionId) > 0 ? datos : null);
  return sesion ? normalizarSesionCaja(sesion) : null;
};

export const normalizarMovimientoCaja = (movimiento = {}) => ({
  id: Number(movimiento.id ?? movimiento.Id) || 0,
  cajaSesionId: Number(movimiento.cajaSesionId ?? movimiento.CajaSesionId) || 0,
  tipo: String(movimiento.tipo ?? movimiento.Tipo ?? ""),
  formaPagoId: Number(movimiento.formaPagoId ?? movimiento.FormaPagoId) || 0,
  formaPago: String(movimiento.formaPago ?? movimiento.FormaPago ?? ""),
  importe: Number(movimiento.importe ?? movimiento.Importe) || 0,
  impactaArqueo: Boolean(movimiento.impactaArqueo ?? movimiento.ImpactaArqueo),
  ventaId: numeroOpcional(movimiento.ventaId ?? movimiento.VentaId),
  operadorId: Number(movimiento.operadorId ?? movimiento.OperadorId) || 0,
  pcId: Number(movimiento.pcId ?? movimiento.PcId ?? movimiento.PCId) || 0,
  fecha: movimiento.fecha ?? movimiento.Fecha ?? null,
  concepto: String(movimiento.concepto ?? movimiento.Concepto ?? ""),
});

export const normalizarReporteCaja = (respuesta = {}) => {
  const datos = respuesta?.data ?? respuesta;
  return {
    ingresos: Number(datos?.ingresos ?? datos?.Ingresos) || 0,
    egresos: Number(datos?.egresos ?? datos?.Egresos) || 0,
    saldoNeto: Number(datos?.saldoNeto ?? datos?.SaldoNeto) || 0,
    lista: (datos?.lista ?? datos?.Lista ?? []).map((movimiento) => ({
      ...normalizarMovimientoCaja(movimiento),
      cajaId: Number(movimiento.cajaId ?? movimiento.CajaId) || 0,
      cajaCodigo: String(movimiento.cajaCodigo ?? movimiento.CajaCodigo ?? ""),
      cajaDescripcion: String(movimiento.cajaDescripcion ?? movimiento.CajaDescripcion ?? ""),
    })),
    error: String(datos?.error ?? datos?.Error ?? respuesta?.error ?? ""),
  };
};

export const normalizarResumenCaja = (resumen = {}) => ({
  sesion: sesionCajaDeRespuesta(resumen),
  ingresos: Number(resumen.ingresos ?? resumen.Ingresos) || 0,
  egresos: Number(resumen.egresos ?? resumen.Egresos) || 0,
  saldoSistema: Number(resumen.saldoSistema ?? resumen.SaldoSistema) || 0,
  formasPago: (resumen.formasPago ?? resumen.FormasPago ?? []).map((item) => ({
    formaPagoId: Number(item.formaPagoId ?? item.FormaPagoId) || 0,
    formaPago: String(item.formaPago ?? item.FormaPago ?? ""),
    total: Number(item.total ?? item.Total) || 0,
  })),
  operadores: (resumen.operadores ?? resumen.Operadores ?? []).map((item) => ({
    operadorId: Number(item.operadorId ?? item.OperadorId) || 0,
    operador: String(item.operador ?? item.Operador ?? ""),
    total: Number(item.total ?? item.Total) || 0,
    cantidadVentas: Number(item.cantidadVentas ?? item.CantidadVentas) || 0,
  })),
  movimientos: (resumen.movimientos ?? resumen.Movimientos ?? []).map(normalizarMovimientoCaja),
  error: String(resumen.error ?? resumen.Error ?? ""),
});

export const sesionAbierta = (sesion) =>
  Number(sesion?.id ?? sesion?.Id) > 0 && String(sesion?.estado ?? sesion?.Estado).toLowerCase() === "abierta";

export const esErrorSesionCaja = (mensaje = "") =>
  /sesi[oó]n.*cerr|caja.*inactiv|pc.*compat|operador.*habilit/i.test(String(mensaje));

export const crearUuidCaja = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === "x" ? r : (r & 3) | 8).toString(16);
  });
};

export const importeMovimientoVisual = (movimiento) =>
  ["egreso", "retiro"].includes(String(movimiento?.tipo).toLowerCase())
    ? -Math.abs(Number(movimiento?.importe) || 0)
    : Number(movimiento?.importe) || 0;

export const puedeAdministrarCajas = ({ operador, permisos } = {}) => {
  if (operador?.EsAdministrador === true || operador?.esAdministrador === true || operador?.Administrador === true) return true;
  if (!Array.isArray(permisos)) return true;
  const codigo = (permiso) => String(
    typeof permiso === "string"
      ? permiso
      : permiso?.Permiso_sCodigo || permiso?.Codigo || permiso?.sCodigo || permiso?.Descripcion || "",
  );
  const habilitado = (permiso) => typeof permiso === "string" ||
    permiso?.Activo === true || permiso?.Habilitado === true || permiso?.Permitido === true;
  return permisos.some((permiso) => {
    const valor = codigo(permiso);
    return (valor === "*" || /caja|pos/i.test(valor) && /admin|config/i.test(valor)) && habilitado(permiso);
  });
};
